import { api } from "@/lib/api";
import {
  ClientDto,
  clientDtoListSchema,
  ClientDtoSchema,
  clientFromDto,
} from "@/models";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useToast } from "../use-toast";

export const getClientsQueryOptions = queryOptions({
  queryKey: ["clients"],
  queryFn: async () => {
    const result: Array<ClientDto> = [];
    let page = 0;
    let hasNextPage = true;
    while (hasNextPage) {
      page += 1;
      const res = await api.get("/api/clients", clientDtoListSchema, {
        query: {
          page: page.toString(),
        },
      });
      result.push(...res.Data);
      hasNextPage = res.PageInfo.HasNextPage;
    }
    return result.map(clientFromDto);
  },
});

export function useGetClients<TERROR extends Error>(opts?: {
  onError?: (error: TERROR) => void;
  enabled?: boolean;
}) {
  const { toast } = useToast();

  const q = useQuery({
    ...getClientsQueryOptions,
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getClientsError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}

export function getClientQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["clients", id],
    queryFn: () =>
      api
        .get("/api/clients/$id", ClientDtoSchema, {
          pathReplace: { $id: id },
        })
        .then(clientFromDto),
  });
}

export function useGetClient<TERROR extends Error>(
  id: string,
  opts?: { onError?: (error: TERROR) => void; enabled?: boolean },
) {
  const { toast } = useToast();
  const q = useQuery({
    ...getClientQueryOptions(id),
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getClientDetailError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}
